import { useEffect, useMemo, useRef, useState } from 'react';
import MuxVideo from '@mux/mux-video-react';

import { cn } from '@blockworks/ui/utils';

import PauseIcon from './icons/pause-icon';
import PlayIcon from './icons/play-icon';

const MuxVideoAdPlayer = ({
    playbackId,
    videoTitle,
    linkTo,
}: {
    playbackId: string;
    videoTitle: string;
    linkTo: string;
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isEnded, setIsEnded] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const hideControlsTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (typeof navigator !== 'undefined') {
            setIsMobile(/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent));
        }
    }, []);

    const startHideControlsTimer = () => {
        if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
        hideControlsTimeout.current = setTimeout(() => {
            setShowControls(false);
        }, 1500);
    };

    const handleButtonClick = () => {
        setShowControls(true);
        startHideControlsTimer();

        if (isPaused) {
            setIsEnded(false);
            setIsPaused(false);
            videoRef.current?.play();
        } else {
            setIsPaused(true);
            videoRef.current?.pause();
        }
    };

    const handleVideoEnd = () => {
        setIsPaused(true);
        setIsEnded(true);
        setShowControls(true);
        startHideControlsTimer();
    };

    useEffect(() => {
        return () => {
            if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current);
        };
    }, []);

    // Overlay is visible if:
    // - On mobile: showControls
    // - On desktop: hovered or ended or showControls
    const memoizedShowOverlay = useMemo(() => {
        return isMobile ? showControls : isHovered || isEnded || showControls;
    }, [isMobile, isHovered, isEnded, showControls]);

    return (
        <div
            className="w-full h-full relative"
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => !isMobile && setIsHovered(false)}
            aria-hidden="true"
        >
            <div
                className={cn('absolute inset-0 bg-black transition-opacity duration-300 opacity-0', {
                    'opacity-50': memoizedShowOverlay,
                })}
            />

            <MuxVideo
                ref={videoRef}
                className="w-full h-full object-cover"
                playbackId={playbackId}
                metadata={{ video_title: videoTitle }}
                autoPlay
                muted
                playsInline
                streamType="on-demand"
                onPlay={() => setIsPaused(false)}
                onEnded={handleVideoEnd}
            />

            <div
                className={cn(
                    `flex flex-col items-center justify-center absolute left-1/2 top-[56%] -translate-x-1/2 -translate-y-1/2 z-50 transition-opacity duration-300 opacity-0`,
                    {
                        'opacity-100': memoizedShowOverlay,
                    },
                )}
            >
                <button
                    onClick={handleButtonClick}
                    aria-label={isPaused ? 'Play video' : 'Pause video'}
                    className="bg-transparent border-none cursor-pointer"
                >
                    {isPaused ? (
                        <PlayIcon className="fill-white/80 hover:fill-white h-16 w-16 md:h-14 md:w-14 lg:h-12 lg:w-12" />
                    ) : (
                        <PauseIcon className="fill-white/80 hover:fill-white h-16 w-16 md:h-14 md:w-14 lg:h-12 lg:w-12" />
                    )}
                </button>
                <a
                    href={linkTo}
                    target="_blank"
                    rel="sponsored nofollow"
                    className="block mt-3 lg:mt-1.5 uppercase text-white bg-brand hover:bg-gray-700 px-3 py-2 lg:px-2 lg:py-1 text-md lg:text-xs transition duration-300"
                >
                    learn more
                </a>
            </div>
        </div>
    );
};

export default MuxVideoAdPlayer;
